<template>
    <div class="safe-froala-wrapper">
        <froala
            v-if="shouldRender"
            :value="computedFroalaValue"
            :tag="tag"
            :config="mergedConfig"
            @update:value="handleUpdate"
            @error="handleError"
        />
    </div>
</template>

<script>
import { stripInlineTextColors, stripThemeBreakingBackgrounds } from '@/utilities/stripInlineTextColors.js';

export default {
    name: 'SafeFroalaWrapper',

    props: {
        value: {
            type: String,
            default: '',
        },

        tag: {
            type: String,
            default: 'textarea',
        },

        config: {
            type: Object,
            default: () => ({}),
        },
    },

    emits: ['update:value', 'initialized', 'error'],

    data() {
        return {
            shouldRender: false,
            internalValue: '',
            isInitialized: false,
            pendingValue: null,
            userIsTyping: false,
            typingTimeout: null,
            froalaValue: '', // Separate value for Froala to prevent unwanted updates
        };
    },

    computed: {
        // Computed property to control what value Froala receives
        computedFroalaValue() {
            // Always bind the editor to its own HTML. Sanitized content is emitted
            // for persistence, but writing it back here makes vue-froala call
            // html.set() and undo.reset() while the user is typing.
            return this.froalaValue;
        },

        mergedConfig() {
            const vm = this;
            const blurHandler = this.config?.events?.blur;
            const parentPasteAfterCleanup = this.config?.events?.['paste.afterCleanup'];

            return {
                ...this.config,
                // Word paste defaults include `color` and `background`. Drop both here:
                // paste cleanup still strips inline text color, and only backgrounds
                // are removed from the value that is saved.
                wordAllowedStyleProps: [
                    'font-family',
                    'font-size',
                    'width',
                    'text-align',
                    'vertical-align',
                    'padding',
                    'margin',
                    'height',
                    'margin-top',
                    'margin-left',
                    'margin-right',
                    'margin-bottom',
                    'text-decoration',
                    'font-weight',
                    'font-style',
                    'text-indent',
                    'border',
                    'border-.*',
                ],
                events: {
                    ...this.config.events,
                    // Must chain the parent's `initialized` (e.g. Editor.vue sets editorInstance + disable).
                    // Using `function` preserves Froala's `this` as the editor instance.
                    initialized(...args) {
                        vm.isInitialized = true;

                        if (vm.pendingValue !== null) {
                            const nextValue = vm.sanitizeEditorValue(vm.pendingValue);
                            vm.internalValue = nextValue;
                            vm.froalaValue = nextValue;
                            vm.pendingValue = null;
                        }

                        const parentInitialized = vm.config?.events?.initialized;
                        if (typeof parentInitialized === 'function') {
                            parentInitialized.apply(this, args);
                        }

                        vm.$emit('initialized', this);
                    },
                    contentChanged: () => {
                        this.markUserTyping();
                    },
                    keydown: () => {
                        this.markUserTyping();
                    },
                    input: () => {
                        this.markUserTyping();
                    },
                    'paste.before': () => {
                        this.markUserTyping();
                    },
                    'paste.afterCleanup': function (clipboardHtml) {
                        vm.markUserTyping();

                        let html = clipboardHtml;
                        if (typeof parentPasteAfterCleanup === 'function') {
                            const result = parentPasteAfterCleanup.call(this, clipboardHtml);
                            if (typeof result === 'string') {
                                html = result;
                            }
                        }

                        return stripInlineTextColors(html);
                    },
                    blur(...args) {
                        blurHandler?.apply(this, args);

                        if (this?.codeView?.isActive?.()) {
                            setTimeout(() => {
                                vm.handleCodeViewBlur(this);
                            }, 0);
                        }
                    },
                },
            };
        },
    },

    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                const nextValue = this.sanitizeEditorValue(newValue);

                // Only update if the new value is actually different from the current internal value
                // This prevents the editor from being cleared when the same value is set during autosave
                if (nextValue !== this.internalValue) {
                    if (this.isInitialized) {
                        this.internalValue = nextValue;
                        // Only update froalaValue if user is not typing
                        if (!this.userIsTyping) {
                            this.froalaValue = nextValue;
                        }
                    } else {
                        this.pendingValue = nextValue;
                    }
                }
            },
        },
    },

    mounted() {
        // Strip backgrounds once on load. Text color stays in the editor so a later save can keep it.
        const initialValue = this.sanitizeEditorValue(this.value);
        this.internalValue = initialValue;
        this.froalaValue = initialValue;

        // Delay rendering to avoid race conditions
        this.$nextTick(() => {
            setTimeout(() => {
                this.shouldRender = true;
            }, 50);
        });
    },

    beforeUnmount() {
        // Clean up timeout
        if (this.typingTimeout) {
            clearTimeout(this.typingTimeout);
        }
    },

    methods: {
        markUserTyping() {
            // User is actively editing
            this.userIsTyping = true;

            // Clear any existing timeout
            if (this.typingTimeout) {
                clearTimeout(this.typingTimeout);
            }

            // Set a timeout to mark user as no longer typing
            this.typingTimeout = setTimeout(() => {
                this.typingTimeout = null;
                this.userIsTyping = false;
                this.resyncEditorWithLatestValue();
            }, 2000); // 2 seconds after last change
        },

        // While the user types, parent value changes only reach internalValue so the
        // editor is not reset mid-keystroke. Once typing stops, push that value into the
        // editor if it no longer matches what the editor shows (e.g. the parent loaded a
        // different record). Compare sanitized HTML so live editor content that only
        // differs by stripped backgrounds is not re-set (no html.set / undo.reset).
        resyncEditorWithLatestValue() {
            if (!this.isInitialized) {
                return;
            }

            if (this.internalValue !== this.sanitizeEditorValue(this.froalaValue)) {
                this.froalaValue = this.internalValue;
            }
        },

        sanitizeEditorValue(value) {
            if (typeof value !== 'string') {
                return value || '';
            }

            return stripThemeBreakingBackgrounds(value);
        },

        handleUpdate(value) {
            const sanitized = typeof value === 'string' ? stripThemeBreakingBackgrounds(value) : value || '';

            // Keep the live editor HTML so vue-froala's value watch matches oldModel.
            if (this.userIsTyping) {
                this.froalaValue = typeof value === 'string' ? value : this.froalaValue;
            }

            if (this.internalValue !== sanitized) {
                this.internalValue = sanitized;
                if (!this.userIsTyping) {
                    this.froalaValue = sanitized;
                }
                this.$emit('update:value', sanitized);
            }
        },

        handleCodeViewBlur(editor) {
            const value = editor?.codeView?.get?.();

            if (typeof value !== 'string') {
                return;
            }

            this.markUserTyping();
            this.handleUpdate(value);
        },

        handleError(error) {
            this.$emit('error', error);
        },
    },
};
</script>

<style scoped>
.safe-froala-wrapper {
    min-height: 60px;
}
:deep(.fr-box) {
    color: black
}
</style>
